import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import useEditor from 'packages/hooks/useEditor';

export const ShotsContext = createContext<any>({});

interface Props {
    children: ReactNode;
}

export function ShotsProvider({ children }: Props) {
    const [version, setVersion] = useState(0);
    const forceUpdate = useCallback(() => setVersion((v) => v + 1), []);
    const { creativeModelFactory, creativeModel } = useEditor() as any;
    const data = useRef<any>({
        shotRenderer: null,
        shotsImages: [],
        shots: [],
    });

    const handleFrameUpdate = useCallback(
        ({ shotIndex, dataUrl }: { shotIndex: number; dataUrl: string }) => {
            data.current.shotsImages[shotIndex] = dataUrl;
            forceUpdate();
        },
        [forceUpdate],
    );

    useEffect(() => {
        const { shotRenderer } = data.current;

        if (!creativeModel || !shotRenderer) {
            return;
        }

        const handler = () => {
            shotRenderer.drawShots().catch(console.error);
        };

        creativeModel.on('dimensionUpdated', handler);
        creativeModel.on('currentPageChange', handler);

        return () => {
            creativeModel.off('dimensionUpdated', handler);
            creativeModel.off('currentPageChange', handler);
        };
    }, [creativeModel, data.current.shotRenderer]);

    useEffect(() => {
        if (!creativeModelFactory) {
            return;
        }

        const shotRenderer = creativeModelFactory.getShotRenderer();
        shotRenderer.setShotSize(32, 32);
        shotRenderer.drawShots().catch(console.error);
        data.current.shotRenderer = shotRenderer;

        shotRenderer.on('frameUpdate', handleFrameUpdate);

        return () => shotRenderer.off('frameUpdate', handleFrameUpdate);
    }, [creativeModelFactory, handleFrameUpdate]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        data.current.shots = creativeModel.getShots();
        const handleShotsReplaced = () => {
            data.current.shots = creativeModel.getShots();
            forceUpdate();
        };

        creativeModel.on('shotsReplaced', handleShotsReplaced);
        creativeModel.on('currentPageChange', handleShotsReplaced);

        return () => {
            creativeModel.off('shotsReplaced', handleShotsReplaced);
            creativeModel.off('currentPageChange', handleShotsReplaced);
        };
    }, [creativeModel, forceUpdate]);

    const value = useMemo(
        () => ({
            ...data.current,
        }),
        [version],
    );

    return <ShotsContext.Provider value={value}>{children}</ShotsContext.Provider>;
}
